import React from 'react';
import { Link } from 'react-router-dom';
import { Check, MapPin, ArrowRight, Battery, Truck, Users, Shield, Zap, Target, Eye, Crosshair } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { companyInfo, homeContent, brandsData } from '../data/mock';

const HomePage = () => {
  return (
    <div className="overflow-hidden bg-white">
      {/* Hero Section with Banner */}
      <section 
        className="relative py-20 lg:py-28 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_176ab01e-388f-4bea-9c6b-49caaa144cc8/artifacts/u5vh6ryc_HomePageBanner.png)' }}
      >
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/40" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-xl text-left">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-6">
              <Zap size={16} className="mr-2" />
              Since {companyInfo.since}
            </div>
            
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-6 drop-shadow-lg">
              {homeContent.hero.headline}
            </h1>
            
            <p className="text-xl text-white/90 leading-relaxed mb-4 drop-shadow">
              {homeContent.hero.subheadline}
            </p>
            
            <p className="text-white/80 mb-10 drop-shadow">
              {homeContent.hero.description}
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-[#c41e3a] hover:bg-[#a01830] text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300">
                  Become a Dealer
                  <ArrowRight size={20} className="ml-2" />
                </Button>
              </Link>
              <Link to="/products">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#1a237e] px-8 py-6 text-lg transition-all duration-300">
                  Explore Products
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Vision */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-white">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-14 h-14 bg-[#1a237e]/10 rounded-full flex items-center justify-center mr-4">
                    <Eye className="w-7 h-7 text-[#1a237e]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#1a237e]">{homeContent.vision.title}</h2>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  {homeContent.vision.content}
                </p>
              </CardContent>
            </Card>

            {/* Mission */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-white">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-14 h-14 bg-[#c41e3a]/10 rounded-full flex items-center justify-center mr-4">
                    <Crosshair className="w-7 h-7 text-[#c41e3a]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#c41e3a]">{homeContent.mission.title}</h2>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  {homeContent.mission.content}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1a237e] mb-4">Brands We Represent</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We proudly associate with reputed national and international brands, delivering consistent quality and dependable service.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {Object.entries(brandsData).map(([key, brand]) => (
              <Link key={key} to={`/products?brand=${key}`}>
                <Card className="group h-full hover:shadow-xl transition-all duration-300 border border-gray-100 shadow-md overflow-hidden bg-white">
                  <CardContent className="p-8 flex flex-col items-center text-center space-y-4">
                    <div className="w-32 h-32 flex items-center justify-center bg-gray-50 rounded-xl p-4 group-hover:scale-105 transition-transform duration-300">
                      <img
                        src={brand.logo}
                        alt={brand.name}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <p className="text-sm text-gray-500">{brand.segment}</p>
                    <span className="inline-flex items-center text-[#c41e3a] text-sm font-medium group-hover:translate-x-1 transition-transform">
                      Learn More <ArrowRight size={16} className="ml-1" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Sen & Co Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1a237e] mb-8">
                Why Choose Sen &amp; Co?
              </h2>
              <div className="space-y-4">
                {homeContent.whyUs.map((item, index) => (
                  <div key={index} className="flex items-start space-x-4 group">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center group-hover:bg-green-200 transition-colors">
                      <Check size={18} className="text-green-600" />
                    </div>
                    <p className="text-gray-700 pt-1">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-6">
              <Card className="p-6 bg-white border-0 shadow-lg hover:shadow-xl transition-shadow">
                <Battery className="w-10 h-10 text-[#c41e3a] mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Multi-Brand</h3>
                <p className="text-sm text-gray-500">Portfolio across conventional &amp; new-age energy</p>
              </Card>
              <Card className="p-6 bg-white border-0 shadow-lg hover:shadow-xl transition-shadow">
                <Users className="w-10 h-10 text-[#1a237e] mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Strong Network</h3>
                <p className="text-sm text-gray-500">Deep dealer &amp; retailer relationships</p>
              </Card>
              <Card className="p-6 bg-white border-0 shadow-lg hover:shadow-xl transition-shadow">
                <Shield className="w-10 h-10 text-orange-500 mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Trust</h3>
                <p className="text-sm text-gray-500">Transparent billing &amp; warranty processes</p>
              </Card>
              <Card className="p-6 bg-white border-0 shadow-lg hover:shadow-xl transition-shadow">
                <Target className="w-10 h-10 text-green-600 mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Long-term Focus</h3>
                <p className="text-sm text-gray-500">Partnerships, not one-time sales</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Our Reach Section */}
      <section className="py-20 bg-[#1a237e] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Our Reach</h2>
            <p className="text-blue-200 max-w-2xl mx-auto">
              Expanding footprint across West Bengal with a strong ground presence
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            {homeContent.reach.map((location, index) => (
              <div
                key={index}
                className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full hover:bg-white/20 transition-colors"
              >
                <MapPin size={18} className="text-[#c41e3a]" />
                <span>{location}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1a237e] mb-4">
              We Don&apos;t Just Distribute Products
            </h2>
            <p className="text-xl text-[#c41e3a] font-semibold">We Build Markets.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 text-center bg-white border border-gray-100 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[#c41e3a]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Battery className="w-8 h-8 text-[#c41e3a]" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">From Batteries</h3>
              <p className="text-gray-600">To mobility solutions</p>
            </Card>
            
            <Card className="p-8 text-center bg-white border border-gray-100 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[#1a237e]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Truck className="w-8 h-8 text-[#1a237e]" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">From City Markets</h3>
              <p className="text-gray-600">To semi-urban and rural networks</p>
            </Card>
            
            <Card className="p-8 text-center bg-white border border-gray-100 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">From Brands</h3>
              <p className="text-gray-600">To businesses</p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Looking to Grow Your Battery or Automotive Business?
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Partner with a distributor that understands your market.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="bg-[#c41e3a] hover:bg-[#a01830] text-white px-8 py-6 text-lg">
                Become a Dealer
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
            <Link to="/products">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg">
                Explore Our Products
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg">
                Contact Our Team
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
